import {
	AssetTreePicker,
	ErrorModal,
	getMomentTz,
	TimeService,
	HelmetDexteel,
} from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Checkbox,
	FormControlLabel,
	TextField,
	Tooltip,
} from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import Snackbar from "@mui/material/Snackbar";
import { createStyles, makeStyles } from "@mui/styles";
import { type Theme } from "@mui/material/styles";
import Alert from "@mui/lab/Alert";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { CraftPicker } from "@/shared/work-orders-modals/components/picker/craftPicker";
import { useTemplatesContext } from "../context/templatesContext";
import type { ParametersNewTemplate } from "../models/ParametersNewTemplate";
import { useUpsertTemplate } from "../hooks/useTemplatesMutations";
import { ScheduleCodePicker } from "./picker/ScheduleCodePicker";
import { DateTimePicker } from "@mui/x-date-pickers";
import { TableTemplateResoures } from "./template-resources/table-template-resoures";
import type { Template } from "../models/Template";
import cronstrue from "cronstrue";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import IconButton from "@mui/material/IconButton";

const moment = getMomentTz();

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiFormLabel-root": {
				transform: "translate(14px, -6px) scale(0.75) ",
				backgroundColor: "#FFFF",
				transition: "none !important",
				left: -3.5,
				padding: "0 5px",
			},
			"& .MuiInputAdornment-root": {
				marginTop: "-8px",
			},
			"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.primary.main,
			},
			"& .MuiFormLabel-root.Mui-focused": {
				color: theme.palette.primary.main,
			},
		},
		title: {
			fontSize: 15,
			margin: "3px 0",
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
		infoLabel: {
			fontSize: 12,
			color: "#757575",
		},
		assetPicker: {
			"& .MuiGrid-item": {
				width: "500px",
			},
		},
		inputEditable: {
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				border: `2px solid ${theme.palette.primary.main}`,
			},
		},
		reponsive: {
			[theme.breakpoints.up("md")]: {
				padding: "0 !important",
			},
		},
		reponsiveTwo: {
			[theme.breakpoints.up("md")]: {
				padding: "0 10px 0 0 !important",
			},
		},
		disabledDatePicker: {
			"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
				{
					borderWidth: "1px !important",
					borderColor: "rgba(0, 0, 0, 0.23)",
				},
			"& .MuiInputBase-input.Mui-disabled": {
				color: "rgba(0, 0, 0, 0.38)",
			},
			"& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
				{
					border: `2px solid ${theme.palette.primary.main}`,
				},
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	data: Template | null;
	initialNewMode?: boolean;
};

export const EditTemplate = ({ show, onHide, data, initialNewMode }: Props) => {
	const {
		state: { INITIAL_VALUE_TEMPLATE_SELECTED },
		state: {
			INITIAL_VALUE_TEMPLATE_SELECTED: {
				TemplateId,
				AssetId,
				AssetName,
				TypeCode,
				Title,
				CraftId,
				DownHours,
				IsNextDownDay,
				PlannedHours,
				NumberOfWorkers,
				Description,
				SchedulingPolicyCode,
				selectedPeriodDays,
				selectedWarningDays,
				selectedExpectedDays,
				selectedCronExpression,
				ScheduleStart,
			},
			rowSelectedTemplate,
		},
		state,
		actions: {
			setSelectedAssetId,
			setSelectedAssetName,
			setRowSelectedTemplate,
			setSelectedTemplate,
		},
	} = useTemplatesContext();

	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("md");
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [editTemplateError, setEditTemplateError] = useState<string>("");
	const [openTemplateResources, setOpenTemplateResources] = useState(false);
	const [newMode, setNewMode] = useState(initialNewMode);
	const [enableScheduleStart, setEnableScheduleStart] = useState<boolean>(true);
	const [assetChanged, setAssetChanged] = useState(false);
	const classes = useStyles();
	const [cronExpression, setCronExpression] = useState("");
	const [isModalOpen, setIsModalOpen] = useState(false);

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors },
		watch,
	} = useForm<ParametersNewTemplate>({
		defaultValues: INITIAL_VALUE_TEMPLATE_SELECTED,
	});
	const description = watch("Description");
	const schedulingPolicyCode = watch("SchedulingPolicyCode");
	const scheduleStart = watch("ScheduleStart");
	// "Is down day" is derived from the schedule type; the form only stores the Next flag.
	const isDownDay = schedulingPolicyCode === "D";

	const { upsertTemplate, isSaving } = useUpsertTemplate({
		onSuccess: (_created, submitData) => {
			setRowSelectedTemplate({ ...rowSelectedTemplate, ...submitData });
			setSelectedTemplate({ ...INITIAL_VALUE_TEMPLATE_SELECTED, ...submitData });
			setAssetChanged(false);
			onHide(true);
		},
		onError: (e) => setEditTemplateError(e.message),
	});
	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const changeSearchAsset = (value: number | null) => {
		if (value !== null) {
			setValue("AssetId", value);
			setSelectedAssetId(value);
			setAssetChanged(true);
		}
	};

	const handleCancel = () => {
		onHide(false);
		setCronExpression("");
		setIsModalOpen(false);
	};

	const onSubmit: SubmitHandler<ParametersNewTemplate> = (data: any) => {
		const submitData = {
			...data,
			CronExpression: cronExpression,
		};
		upsertTemplate(submitData);
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("ScheduleStart", date);
	};

	const handleTemplateResourcesDialog = () => {
		setOpenTemplateResources(true);
	};

	useEffect(() => {
		if (schedulingPolicyCode === "P") {
			setEnableScheduleStart(false);
		} else {
			setEnableScheduleStart(true);
		}
		// "Next down day" only applies to down-day templates; reset it otherwise.
		if (schedulingPolicyCode !== "D") {
			setValue("IsNextDownDay", false);
		}
	}, [schedulingPolicyCode]);

	useEffect(() => {
		setValue("TemplateId", TemplateId);
		setValue("Title", Title);
		setValue("AssetId", AssetId);
		setValue("AssetName", AssetName);
		setValue("CraftId", CraftId);
		setValue("TypeCode", TypeCode);
		setValue("selectedPeriodDays", selectedPeriodDays);
		setValue("selectedExpectedDays", selectedExpectedDays);
		setValue("selectedWarningDays", selectedWarningDays);
		setValue("PlannedHours", PlannedHours);
		setValue("DownHours", DownHours);
		setValue("IsNextDownDay", IsNextDownDay);
		setValue("NumberOfWorkers", NumberOfWorkers);
		setValue("Description", Description);
		setValue("SchedulingPolicyCode", SchedulingPolicyCode);
		setValue("ScheduleStart", ScheduleStart);
		setNewMode(initialNewMode);
		setAssetChanged(false);
		setCronExpression(selectedCronExpression as string);
	}, [show, initialNewMode]);

	return (
		<>
			{show && (
				<HelmetDexteel title={`Edit Template${Title ? ` - ${Title}` : ""}`} />
			)}
			<Grid2 container>
				<Grid2>
					<Dialog
						fullWidth={fullWidth}
						maxWidth={maxWidth}
						open={show}
						onClose={() => handleCancel()}
					>
						<form
							onSubmit={handleSubmit(onSubmit)}
							style={{ paddingTop: "8px" }}
						>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<DialogTitle
									style={{
										padding: "0px 24px 0px 24px",
										margin: "0px",
										width: "100%",
									}}
								>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
											}}
										>
											<h1
												style={{
													fontSize: "1.7em",
													margin: "0px",
													padding: "8px 0px",
												}}
											>
												EDIT TEMPLATE
											</h1>
											{isDownDay && (
												<Tooltip title="This template is scheduled for a plant down day">
													<ScheduleIcon color="secondary" />
												</Tooltip>
											)}
										</div>
									</Grid2>
								</DialogTitle>
							</Grid2>

							<DialogContent
								style={{ padding: "0 24px", overflow: "visible" }}
								dividers
							>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									TEMPLATE TITLE
								</h6>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="Title"
											control={control}
											rules={{
												validate: (value) => value !== "",
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													label="Title"
													variant="outlined"
													fullWidth
													error={!!errors.Title}
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
										{errors.Title?.type === "required" && (
											<span className={classes.errorLabel}>
												Title is required
											</span>
										)}
									</Grid2>
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
									style={{ margin: "5px 0 -5px" }}
								>
									MAIN PROPERTIES
								</h6>
								<Grid2 container>
									<Grid2
										container
										style={{ marginTop: 10, padding: 0 }}
										className={classes.assetPicker}
										size={{ md: 12, xs: 12 }}
									>
										<Grid2
											style={{ padding: "0 10px 0 0" }}
											className={classes.inputEditable}
											size={{ md: 6, xs: 12 }}
										>
											<Controller
												name="AssetId"
												control={control}
												render={({ field: { value } }) => (
													<AssetTreePicker
														label="Asset"
														value={value || 0}
														onChange={changeSearchAsset}
													/>
												)}
											/>
										</Grid2>

										<Grid2
											style={{ padding: "0 10px 0 0" }}
											size={{ md: 4, xs: 6 }}
										>
											<Controller
												name="CraftId"
												control={control}
												render={({ field: { onChange, value } }) => (
													<CraftPicker
														value={value as number}
														onChange={onChange}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ md: 2, xs: 6 }}>
											<Controller
												name="TypeCode"
												control={control}
												render={({}) => (
													<TextField
														label="Type"
														variant="outlined"
														disabled
														value={state.INITIAL_VALUE_TEMPLATE.TypeCode}
														fullWidth
														margin="dense"
														autoComplete="off"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									SCHEDULING
								</h6>
								<Grid2 container size={{ md: 12, xs: 12 }}>
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										size={{ md: schedulingPolicyCode === "D" ? 4 : 3, xs: 6 }}
									>
										<Controller
											name="SchedulingPolicyCode"
											control={control}
											render={({ field: { onChange, value } }) => (
												<ScheduleCodePicker
													value={value}
													onChange={(newValue: string) => {
														onChange(newValue);
													}}
													cronExpression={cronExpression}
													onCronExpressionChange={setCronExpression}
													isModalOpen={isModalOpen}
													setIsModalOpen={setIsModalOpen}
													hideScheduleStart={setEnableScheduleStart}
													onScheduleStartChange={handleScheduleStartChange}
													scheduleStart={scheduleStart}
												/>
											)}
										/>
									</Grid2>
									{schedulingPolicyCode === "D" && (
											<Grid2
												style={{
													padding: "0 10px 0 0",
													display: "flex",
													alignItems: "center",
												}}
												size={{ md: 8, xs: 12 }}
											>
												<Controller
													name="IsNextDownDay"
													control={control}
													render={({ field: { onChange, value } }) => (
														<FormControlLabel
															control={
																<Checkbox
																	color="primary"
																	checked={!!value}
																	onChange={(e) => onChange(e.target.checked)}
																/>
															}
															label="Next down day"
														/>
													)}
												/>
											</Grid2>
										)}
										{schedulingPolicyCode !== "D" && (
										<>
									{enableScheduleStart && (
										<Grid2
											style={{ padding: "0 10px 0 0" }}
											size={{ md: 2, xs: 6 }}
										>
											<Controller
												name="selectedPeriodDays"
												control={control}
												render={({ field: { onChange, value } }) => (
													<TextField
														className={`${classes.root} ${classes.inputEditable}`}
														type="number"
														label="Period (days)"
														variant="outlined"
														fullWidth
														margin="dense"
														autoComplete="off"
														value={value}
														onChange={(e) => onChange(e)}
													/>
												)}
											/>
										</Grid2>
									)}
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										className={classes.root}
										size={{
											md: enableScheduleStart ? 2.75 : 5,
											xs: enableScheduleStart ? 6 : 6,
										}}
									>
										<Controller
											name="ScheduleStart"
											control={control}
											render={({ field }) => (
												<>
													<DateTimePicker
														label="Schedule Start"
														timezone={
															enableScheduleStart
																? TimeService.getInstance().getServerTimeZone()
																: "UTC"
														}
														format={
															enableScheduleStart
																? "MM/DD/YYYY HH:mm"
																: "MM/DD/YYYY"
														}
														timeSteps={{ minutes: 1 }}
														value={moment(field.value) as any}
														onChange={field.onChange}
														inputRef={field.ref}
														disabled={schedulingPolicyCode === "P"}
														slotProps={{
															textField: {
																fullWidth: true,
																variant: "outlined",
																margin: "dense",
																size: "small",
																error: !!errors.ScheduleStart,
																className: classes.disabledDatePicker,
															},
														}}
													/>
													{errors.ScheduleStart && (
														<span className={classes.errorLabel}>
															{errors.ScheduleStart.message}
														</span>
													)}
												</>
											)}
										/>
									</Grid2>
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										size={{ md: 2.25, xs: 6 }}
									>
										<Controller
											name="selectedExpectedDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Expected (days)"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid2>

									<Grid2
										style={{ padding: "0" }}
										size={{ md: 2, xs: 6 }}
									>
										<Controller
											name="selectedWarningDays"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Warning (days)"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid2>
									</>
									)}
									{schedulingPolicyCode === "P" && (
										<Grid2 size={{ md: 12, xs: 12 }}>
											<TextField
												label="Schedule"
												variant="outlined"
												fullWidth
												margin="dense"
												placeholder="Choose your schedule"
												value={
													cronExpression?.length > 0
														? cronstrue.toString(cronExpression as string)
														: ""
												}
												slotProps={{
													input: {
														readOnly: true,
														endAdornment: (
															<IconButton
																edge="end"
																onClick={() => setIsModalOpen(true)}
																size="small"
															>
																<EditIcon />
															</IconButton>
														),
													},
												}}
											/>
										</Grid2>
									)}
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									WORK TIME
								</h6>
								<Grid2 container size={{ md: 12, xs: 12 }}>
									<Grid2
										style={{ padding: "0 10px 0 0" }}
										size={{ md: 4, xs: 6 }}
									>
										<Controller
											name="PlannedHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													type="number"
													label="Planned Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid2>
									<Grid2
										className={classes.reponsiveTwo}
										style={{ padding: "0 0 0 0 !important" }}
										size={{ md: 4, xs: 6 }}
									>
										<Controller
											name="DownHours"
											control={control}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={`${classes.root} ${classes.inputEditable}`}
													type="number"
													label="Down time Hours"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) => onChange(e)}
												/>
											)}
										/>
									</Grid2>

									<Grid2
										className={classes.reponsive}
										style={{ padding: "0 0 0 10px" }}
										size={{ md: 4, xs: 6 }}
									>
										<Controller
											name="NumberOfWorkers"
											control={control}
											rules={{
												min: 1,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													className={classes.inputEditable}
													type="number"
													label="Workers Needed"
													variant="outlined"
													error={!!errors.NumberOfWorkers}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													onChange={(e) =>
														Number.parseInt(e.target.value as any) >= 1 &&
														onChange(e)
													}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
								<h6
									className={classes.title}
									id="contained-modal-title-vcenter"
								>
									MISC
								</h6>
								<Grid2
									style={{ margin: "5px 0 0", padding: "0px" }}
									size={{ md: 12, xs: 12 }}
								>
									<Controller
										name="Description"
										control={control}
										render={({ field: { value, onChange } }) => (
											<TextField
												className={`${classes.root} ${classes.inputEditable}`}
												style={{ margin: "0" }}
												size="medium"
												maxRows={5}
												label="Description"
												multiline
												variant="outlined"
												fullWidth
												autoComplete="off"
												value={value}
												onChange={(evt) => {
													if (evt.target.value.length <= 400) onChange(evt);
												}}
											/>
										)}
									/>
									<Grid2 container style={{ position: "relative" }}>
										<Grid2>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) >= 1 &&
													(description?.length as number) !== 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>{`${description?.length}/400 max.`}</span>
											<span
												className={classes.infoLabel}
												style={
													(description?.length as number) == 400
														? {
																marginLeft: 5,
																visibility: "visible",
																position: "absolute",
																left: 0,
															}
														: { visibility: "hidden" }
												}
											>
												Max. 400
											</span>
										</Grid2>
									</Grid2>
								</Grid2>
							</DialogContent>
							<DialogActions
								sx={{
									padding: "0px 24px",
									marginTop: "24px",
									paddingBottom: "16px",
								}}
							>
								<Grid2 container spacing={2}>
									<Grid2
										sx={{ display: "flex", alignItems: "center" }}
										title={
											assetChanged
												? "You need to save to be able to manage resources"
												: ""
										}
										size={{ md: 4, xs: 4 }}
									>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											disabled={assetChanged}
											onClick={handleTemplateResourcesDialog}
										>
											MANAGE RESOURCES
										</Button>
									</Grid2>
									<Grid2
										container
										justifyContent="flex-end"
										spacing={2}
										sx={{ margin: 0 }}
										size={{ md: 8, xs: 8 }}
									>
										<Grid2 size={{ md: 6, xs: 6 }}>
											<Button
												fullWidth
												variant="contained"
												color="inherit"
												onClick={handleCancel}
											>
												CANCEL
											</Button>
										</Grid2>
										<Grid2 sx={{ paddingRight: 0 }} size={{ md: 6, xs: 6 }}>
											<Button
												fullWidth
												startIcon={
													isSaving && <CircularProgress size="1rem" />
												}
												disabled={
													isSaving ||
													(schedulingPolicyCode === "P" &&
														(!cronExpression ||
															cronExpression.trim().length === 0))
												}
												variant="contained"
												color="primary"
												type="submit"
											>
												SAVE
											</Button>
										</Grid2>
									</Grid2>
								</Grid2>
							</DialogActions>
						</form>
					</Dialog>
					<TableTemplateResoures
						show={openTemplateResources}
						onHide={(shouldUpdate: boolean) => {
							setOpenTemplateResources(false);
						}}
						AssetId={AssetId}
						TemplateId={TemplateId}
						newMode={initialNewMode}
						Title={Title as string}
						AssetName={(AssetName as string)?.replace(/.*\/(.+)$/, "$1")}
						TypeCode={TypeCode as string}
						CraftId={CraftId as number}
					/>
				</Grid2>
			</Grid2>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="success" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={editTemplateError}
				onHide={() => setEditTemplateError("")}
			/>
		</>
	);
};
